
import React, { useState, useEffect } from 'react'
import { X, FileText, User, Calendar, DollarSign, Building, PenTool } from 'lucide-react'
import { useContratos } from '../hooks/useContratos'

interface ContratoFormProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: any) => Promise<void>
  initialData?: any
  title: string
  orcamentoData?: any
}

const ContratoForm: React.FC<ContratoFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  title,
  orcamentoData
}) => {
  const { templates, getOrcamentosAprovados } = useContratos()
  
  const [formData, setFormData] = useState({
    orcamentoId: '',
    nomeCliente: '',
    contatoCliente: '',
    nomeProjeto: '',
    descricaoProjeto: '',
    status: 'em_desenvolvimento' as const,
    dataEmissao: new Date().toISOString().split('T')[0],
    dataValidade: '',
    valorTotal: 0,
    etapas: [] as any[],
    templateId: '',
    assinaturaEmpresa: '',
    assinaturaCliente: '',
    observacoes: '',
    clausulasEspeciais: '',
    condicoesGerais: ''
  })

  const [orcamentosAprovados, setOrcamentosAprovados] = useState<any[]>([])

  // Load orçamentos aprovados
  useEffect(() => {
    const loadOrcamentosAprovados = async () => {
      const orcamentos = await getOrcamentosAprovados()
      setOrcamentosAprovados(orcamentos)
    }
    if (isOpen) {
      loadOrcamentosAprovados()
    }
  }, [isOpen, getOrcamentosAprovados])

  // Initialize form with orçamento data if provided
  useEffect(() => {
    if (orcamentoData) {
      const dataValidade = new Date()
      dataValidade.setDate(dataValidade.getDate() + 30) // 30 dias de validade padrão

      setFormData(prev => ({
        ...prev,
        orcamentoId: orcamentoData._id,
        nomeCliente: orcamentoData.nomeCliente,
        contatoCliente: orcamentoData.contatoCliente,
        nomeProjeto: orcamentoData.nomeProjeto,
        descricaoProjeto: orcamentoData.descricao || '',
        valorTotal: orcamentoData.valorTotal,
        etapas: orcamentoData.etapas || [],
        dataValidade: dataValidade.toISOString().split('T')[0]
      }))
    }
  }, [orcamentoData])

  // Initialize form with existing data
  useEffect(() => {
    if (initialData) {
      setFormData({
        orcamentoId: initialData.orcamentoId || '',
        nomeCliente: initialData.nomeCliente || '',
        contatoCliente: initialData.contatoCliente || '',
        nomeProjeto: initialData.nomeProjeto || '',
        descricaoProjeto: initialData.descricaoProjeto || '',
        status: initialData.status || 'em_desenvolvimento',
        dataEmissao: initialData.dataEmissao?.split('T')[0] || new Date().toISOString().split('T')[0],
        dataValidade: initialData.dataValidade?.split('T')[0] || '',
        valorTotal: initialData.valorTotal || 0,
        etapas: initialData.etapas || [],
        templateId: initialData.templateId || '',
        assinaturaEmpresa: initialData.assinaturaEmpresa || '',
        assinaturaCliente: initialData.assinaturaCliente || '',
        observacoes: initialData.observacoes || '',
        clausulasEspeciais: initialData.clausulasEspeciais || '',
        condicoesGerais: initialData.condicoesGerais || ''
      })
    }
  }, [initialData])

  // Handle orçamento selection
  const handleOrcamentoSelect = (orcamentoId: string) => {
    const orcamento = orcamentosAprovados.find(o => o._id === orcamentoId)
    if (orcamento) {
      const dataValidade = new Date()
      dataValidade.setDate(dataValidade.getDate() + 30)

      setFormData(prev => ({
        ...prev,
        orcamentoId: orcamento._id,
        nomeCliente: orcamento.nomeCliente,
        contatoCliente: orcamento.contatoCliente,
        nomeProjeto: orcamento.nomeProjeto,
        descricaoProjeto: orcamento.descricao || '',
        valorTotal: orcamento.valorTotal,
        etapas: orcamento.etapas || [],
        dataValidade: dataValidade.toISOString().split('T')[0]
      }))
    }
  }

  // Handle template selection
  const handleTemplateSelect = (templateId: string) => {
    const template = templates.find(t => t._id === templateId)
    if (template) {
      const dataValidade = new Date()
      dataValidade.setDate(dataValidade.getDate() + template.prazoValidadePadrao)

      setFormData(prev => ({
        ...prev,
        templateId: template._id,
        clausulasEspeciais: template.clausulasEspeciais,
        condicoesGerais: template.condicoesGerais,
        dataValidade: dataValidade.toISOString().split('T')[0]
      }))
    }
  }

  // Handle submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    const submitData = {
      ...formData,
      dataEmissao: new Date(formData.dataEmissao).toISOString(),
      dataValidade: new Date(formData.dataValidade).toISOString(),
      valorTotal: Number(formData.valorTotal)
    }

    await onSubmit(submitData)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-5xl max-h-[95vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-6 z-10">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X size={24} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Origem do Contrato */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <FileText size={20} className="text-blue-600" />
              Origem do Contrato
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Orçamento Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Orçamento Aprovado (Opcional)
                </label>
                <select
                  value={formData.orcamentoId}
                  onChange={(e) => handleOrcamentoSelect(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Criar contrato direto...</option>
                  {orcamentosAprovados.map(orcamento => (
                    <option key={orcamento._id} value={orcamento._id}>
                      {orcamento.numero} - {orcamento.nomeCliente}
                    </option>
                  ))}
                </select>
              </div>

              {/* Template Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Template (Opcional)
                </label>
                <select
                  value={formData.templateId}
                  onChange={(e) => handleTemplateSelect(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Criar do zero...</option>
                  {templates.filter(t => t.ativo).map(template => (
                    <option key={template._id} value={template._id}>
                      {template.nome} - {template.categoria}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Informações do Cliente */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <User size={20} className="text-gray-600" />
              Informações do Cliente
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome do Cliente *
                </label>
                <input
                  type="text"
                  value={formData.nomeCliente}
                  onChange={(e) => setFormData(prev => ({ ...prev, nomeCliente: e.target.value }))}
                  required
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contato do Cliente
                </label>
                <input
                  type="text"
                  value={formData.contatoCliente}
                  onChange={(e) => setFormData(prev => ({ ...prev, contatoCliente: e.target.value }))}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Informações do Projeto */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Building size={20} className="text-gray-600" />
              Informações do Projeto
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome do Projeto *
                </label>
                <input
                  type="text"
                  value={formData.nomeProjeto}
                  onChange={(e) => setFormData(prev => ({ ...prev, nomeProjeto: e.target.value }))}
                  required
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Valor Total *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.valorTotal}
                  onChange={(e) => setFormData(prev => ({ ...prev, valorTotal: Number(e.target.value) }))}
                  required
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descrição do Projeto *
              </label>
              <textarea
                value={formData.descricaoProjeto}
                onChange={(e) => setFormData(prev => ({ ...prev, descricaoProjeto: e.target.value }))}
                required
                rows={4}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Informações do Contrato */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Calendar size={20} className="text-gray-600" />
              Informações do Contrato
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as any }))}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="em_desenvolvimento">Em Desenvolvimento</option>
                  <option value="enviado">Enviado</option>
                  <option value="aprovado">Aprovado</option>
                  <option value="perdido">Perdido</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data de Emissão *
                </label>
                <input
                  type="date"
                  value={formData.dataEmissao}
                  onChange={(e) => setFormData(prev => ({ ...prev, dataEmissao: e.target.value }))}
                  required
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data de Validade *
                </label>
                <input
                  type="date"
                  value={formData.dataValidade}
                  onChange={(e) => setFormData(prev => ({ ...prev, dataValidade: e.target.value }))}
                  required
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Assinaturas */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <PenTool size={20} className="text-gray-600" />
              Assinaturas
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assinatura da Empresa *
                </label>
                <textarea
                  value={formData.assinaturaEmpresa}
                  onChange={(e) => setFormData(prev => ({ ...prev, assinaturaEmpresa: e.target.value }))}
                  required
                  rows={3}
                  placeholder="Nome completo, cargo, CPF/CNPJ da empresa"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assinatura do Cliente *
                </label>
                <textarea
                  value={formData.assinaturaCliente}
                  onChange={(e) => setFormData(prev => ({ ...prev, assinaturaCliente: e.target.value }))}
                  required
                  rows={3}
                  placeholder="Nome completo, cargo, CPF/CNPJ do cliente"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Cláusulas e Condições */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-4">Cláusulas e Condições</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cláusulas Especiais
                </label>
                <textarea
                  value={formData.clausulasEspeciais}
                  onChange={(e) => setFormData(prev => ({ ...prev, clausulasEspeciais: e.target.value }))}
                  rows={3}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Condições Gerais
                </label>
                <textarea
                  value={formData.condicoesGerais}
                  onChange={(e) => setFormData(prev => ({ ...prev, condicoesGerais: e.target.value }))}
                  rows={3}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Observações
                </label>
                <textarea
                  value={formData.observacoes}
                  onChange={(e) => setFormData(prev => ({ ...prev, observacoes: e.target.value }))}
                  rows={3}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-4 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              {initialData ? 'Atualizar' : 'Criar'} Contrato
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default ContratoForm
